import { useMemo } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'

import {
  FBPageInnerTabsContainer,
  FBPageTableContainer,
} from 'components/FbUI/Page/styles'
import { UnderlineTabs } from 'components/FbUI/UnderlineTabs'
import { UserTable } from 'features/settings/components/UserTable'
import { InvitationsTable } from 'features/settings/components/InvitationsTable'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { useAuth } from 'context/authentication/useAuth'

type TeamTab = 'users' | 'invitations'

const DEFAULT_TAB: TeamTab = 'users'

export function UsersSettingsPage() {
  const params = useParams()
  const navigate = useNavigate()
  const tab = (params.tab as TeamTab) ?? DEFAULT_TAB
  const featureFlags = useFeatureFlagService()
  const { isAdmin } = useAuth()

  const tabs = useMemo<{ key: TeamTab; title: string }[]>(() => {
    const allTabs: { key: TeamTab; title: string }[] = [
      { key: 'users', title: 'Users' },
      ...(featureFlags.enableUserTypes && isAdmin
        ? ([{ key: 'invitations', title: 'Invitations' }] as const)
        : []),
    ]
    return allTabs
  }, [featureFlags.enableUserTypes, isAdmin])

  if (params.tab && !tabs.some((t) => t.key === params.tab)) {
    return <Navigate to={`/settings/team/${DEFAULT_TAB}`} replace />
  }

  return (
    <div className="w-full h-full flex flex-col flex-1 min-h-0 bg-surface-page">
      <FBPageInnerTabsContainer>
        <UnderlineTabs<TeamTab>
          tabs={tabs}
          activeKey={tab}
          onChange={(value) => navigate(`/settings/team/${value}`)}
          idPrefix="team-settings"
          aria-label="Team settings tabs"
        />
      </FBPageInnerTabsContainer>
      <FBPageTableContainer className="bg-surface-page" hide={tab !== 'users'}>
        <UserTable />
      </FBPageTableContainer>
      <FBPageTableContainer
        className="bg-surface-page"
        hide={tab !== 'invitations'}
      >
        <InvitationsTable />
      </FBPageTableContainer>
    </div>
  )
}
